import { Box } from "@mui/material";
import { PrimaryNav } from "@teamworksdev/nav";
import { useEnvironmentType } from "apps/web/src/contexts/useEnvType";
import { useReportingUserIdentify } from "apps/web/src/utils/reporting/useReportingUserIdentiy";
import { Route, Routes } from "react-router-dom-v5-compat";
import { NutritionWhiteIcon } from "shared-ui/global/Icons";
import { useNutritionFlags } from "../../../hooks/useNutritionFlags";
import { useLogout, useUser } from "../../../utils/tokens";
import { DevRouter } from "../Dev/DevRouter";
import { InactivityWarning } from "../InactivityWarning";
import { getOrgUserDetails } from "../OrgUtils";
import {
  DEFAULT_USER_NAME,
  OrgAndStaffRouterProps,
  getStaffPrimaryNavLinks,
  helperMenuLinks,
  staffAdditionalSettingsLinks,
  staffPrimaryNavTeam,
  tacticalHelperMenuLinks,
} from "../PrimaryNavUtils";
import { StaffRoute } from "../ProtectedRoutes";
import { RecipeRouter } from "./Food/RecipeRouter";
import { FoodManagementRouter } from "./FoodManagement/FoodManagementRouter";
import { FormsAndScoresRouter } from "./FormsAndScores/FormsAndScoresRouter";
import { MealPlanManagementRouter } from "./MealPlanManagement/MealPlanManagementRouter";
import { OrgRouter } from "./Org/OrgRouter";
import { OrgManagementRouter } from "./OrgManagement/OrgManagementRouter";
import { RestaurantRouter } from "./Restaurant/RestaurantRouter";
import { RestaurantManagementRouter } from "./RestaurantManagement/RestaurantManagementRouter";
import { SettingsRouter } from "./Settings/SettingsRouter";

export const StaffRouter = ({ orgs, onOrgChange }: OrgAndStaffRouterProps) => {
  useReportingUserIdentify({ forStaff: true });
  const logout = useLogout();
  const user = useUser();
  const orgUserDetails = getOrgUserDetails(user);
  const { userName } = orgUserDetails;

  const { isCommercial, isTactical } = useEnvironmentType();

  const { isTeamworksUserManagementEnabled, showAxleUpsertTab } = useNutritionFlags();
  const logo = <NutritionWhiteIcon sx={{ alignSelf: "center", width: 40, height: 40 }} viewBox="0 0 20 20" />;

  return (
    <Box sx={{ flex: 1, display: "flex" }}>
      <PrimaryNav
        links={getStaffPrimaryNavLinks()}
        app="nutrition"
        logo={logo}
        team={staffPrimaryNavTeam}
        teams={orgs}
        user={{ name: userName || DEFAULT_USER_NAME }}
        additionalSettingsLinks={staffAdditionalSettingsLinks}
        onTeamChange={onOrgChange}
        onLogout={logout}
        hasHelpCenter={isCommercial}
        hasLearningPortal={isCommercial}
        appSpecificHelpSubmenu={{
          links: isCommercial ? helperMenuLinks : tacticalHelperMenuLinks,
          hideDivider: true,
        }}
      />
      <Box sx={{ flex: 1, overflowY: "hidden", backgroundColor: "black", pt: 1 }}>
        <Box
          sx={{
            flex: 1,
            height: "100%",
            backgroundColor: ({ palette: { mode } }) => (mode === "light" ? "white" : "black"),
            borderTopLeftRadius: 12,
            p: 3,
          }}
        >
          {isTactical && <InactivityWarning logout={logout} />}

          <Routes>
            <Route element={<StaffRoute />}>
              <Route
                path="/*"
                index
                element={
                  <OrgManagementRouter
                    withShowAxleUpsertTab={isTactical ? true : showAxleUpsertTab}
                    withTUM={isTeamworksUserManagementEnabled}
                  />
                }
              />
              <Route
                path="org-management/*"
                element={
                  <OrgManagementRouter
                    withShowAxleUpsertTab={isTactical ? true : showAxleUpsertTab}
                    withTUM={isTeamworksUserManagementEnabled}
                  />
                }
              />
              <Route path="food-management/*" element={<FoodManagementRouter />} />
              <Route path="recipe/*" element={<RecipeRouter />} />
              <Route path="restaurant-management/*" element={<RestaurantManagementRouter />} />
              <Route path="meal-plan-management/*" element={<MealPlanManagementRouter />} />
              <Route path="forms-and-scores/*" element={<FormsAndScoresRouter />} />
              <Route path="dev/*" element={<DevRouter org={false} />} />
              <Route path="restaurants/:restaurantId/*" element={<RestaurantRouter />} />
              <Route path="orgs/:orgId/*" element={<OrgRouter />} />
              <Route path="settings/*" element={<SettingsRouter />} />
            </Route>
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};
